import { AfterViewInit, Component, ElementRef, EventEmitter, Injector, Input, OnInit, Output, Renderer2, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { TemplateMessageObject } from '@trendbuild/trend-cloud-api';
import { AbstractComponent, CardPriorityEnum, Channel, ContactGroup, ContactGroupService, Department, DepartmentService, Funnel, FunnelService, LeadSource, LeadSourceService, MetadataMessage, Pager, PRIORITY_LABEL, Product, ProductService, Stage, StageService, Tag, TagService, Team, TeamService, Traduct, UploadTypeEnum, User, UserService, WebhookIntegrationItemType, WebhookIntegrations } from 'lib-trend-core';
import { takeUntil } from 'rxjs';
import { RcsVariableOption } from '../../../rcs-message/rcs-message-editor/rcs-message-editor.component';

@Component({
    selector: 'webhook-integrations-item-component',
    templateUrl: './webhook-integrations-item.v2.component.html',
    // templateUrl: './webhook-integrations-item.component.html',
    styleUrls: ['./webhook-integrations-item.v2.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class WebhookIntegrationsItemComponent extends AbstractComponent implements OnInit, AfterViewInit {

  @Input() itemForm!: FormGroup;
  @Input() index!: number;
  @Input() webhookRequests: object;
  @Input() channel!: Channel;
  @Input() isActive = false;
  @Input() webhookIntegrations: WebhookIntegrations;
  @Output() remove = new EventEmitter<number>();

  selectedTemplate: TemplateMessageObject;

  showPreviewTemplate: boolean = false;

  showContainerEmoji: boolean = false;
  translator!: Traduct;
  keywords: string[] = [];

  tags: Tag[] = [];
  departments: Department[] = [];
  users: User[] = [];
  products: Product[] = [];
  usersByDepartment: User[] = [];
  groups: Array<ContactGroup> = new Array<ContactGroup>();
  funnels: Array<Funnel> = new Array<Funnel>();
  stages: Array<Stage> = new Array<Stage>();
  leadSources: Array<LeadSource> = new Array<LeadSource>();
  allLeadSources: Array<LeadSource> = new Array<LeadSource>();
  filterControlDeparment: FormControl = new FormControl('');

  @ViewChild('messageTextarea') messageTextarea: ElementRef;
  @ViewChild("emojiContainer", { static: false, read: ElementRef }) emojiContainer!: ElementRef

  private clearTimeout
  private clickOutsideListener

  pagerDepartment: Pager<Department> = new Pager<Department>({ perPage: 50 });

  priorities;

  constructor(
    injector: Injector,
    public departmentService: DepartmentService,
    public tagService: TagService,
    public teamService: TeamService,
    private renderer: Renderer2,
    private groupsService: ContactGroupService,
    private funnelService: FunnelService,
    private stageService: StageService,
    private userService: UserService,
    private productService: ProductService,
    private leadSourceService: LeadSourceService,
  ) {
    super(injector);
  }

  ngOnInit(): void {
    if (this.itemForm.get('type').value === WebhookIntegrationItemType.START_ATTENDANCE ||
      this.itemForm.get('type').value === WebhookIntegrationItemType.TRANSFER_ATTENDANCE) {
      this.loadUsersByDepartment();
    }
    if (this.itemForm.get('type').value === WebhookIntegrationItemType.CREATE_OPPORTUNITY) {
      this.priorities = Object.values(CardPriorityEnum).map(v => ({ value: v, label: PRIORITY_LABEL[v] }));
      this.loadStagesByFunnel();
    }
    if (this.itemForm.get('type').value === WebhookIntegrationItemType.ADD_TEMPLATE) {
      this.selectedTemplate = this.itemForm.get('template').value;
      if (this.selectedTemplate) {
        this.showPreviewTemplate = true;
      } else {
        this.showPreviewTemplate = false;
      }
    }
  }

  ngAfterViewInit(): void {
    if (this.isAddTag() || this.isRemoveTag()) {
      this.loadTags();
    }
    if (this.isStartAttendance() || this.isTransferAttendance()) {
      this.loadDepartments();
    }
    if (this.isAddContactGroup() || this.isRemoveContactGroup()) {
      this.loadGroups();
    }
    if (this.isCreateOpportunity()) {
      this.loadFunnels();
      this.loadUsers();
      this.loadProducts();
    }
    if (this.isOriginLead()) {
      this.loadLeadSources();
    }
  }

  addValue(value: string): void {
    let valueForm = this.itemForm.get('value').value;
    if (!valueForm) {
      valueForm = '';
    }
    valueForm += '{{' + value + '}}';
    this.itemForm.get('value').setValue(valueForm);
  }

  deleteComponent(): void {
    this.remove.emit(this.index);
  }

  onSelectTemplate(template: TemplateMessageObject) {
    this.selectedTemplate = template
    this.showPreviewTemplate = true
    this.itemForm.get('template').setValue(this.selectedTemplate)
  }

  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Backspace') {
      const textarea = event.target as HTMLTextAreaElement;
      const cursorPosition = textarea.selectionStart;
      let value = this.itemForm.get('value').value;
      const textBeforeCursor = value.slice(0, cursorPosition);

      if (textBeforeCursor.endsWith('}}')) {
        event.preventDefault();

        const startIndex = textBeforeCursor.lastIndexOf('{{');

        if (startIndex !== -1) {
          this.itemForm.get('value').setValue(
            textBeforeCursor.slice(0, startIndex) +
            value.slice(cursorPosition));
        }
      }
    }
  }

  onPaste(event: ClipboardEvent) {
    event.preventDefault();
    const clipboardData = event.clipboardData || (window as any).clipboardData;
    let pastedData = clipboardData.getData('text/html') || clipboardData.getData('text/plain');

    pastedData = pastedData
      .replace(/<p[^>]*>/g, '\n')
      .replace(/<\/p>/g, '\n\n')
      .replace(/<li[^>]*>/g, '')
      .replace(/<\/li>/g, '\n')
      .replace(/<\/?html[^>]*>/g, '')
      .replace(/<\/?body[^>]*>/g, '')
      .replace(/<!--.*?-->/g, '')
      .replace(/<\/?br[^>]*>/g, '\n')
      .replace(/<\/?strong[^>]*>/g, '*')
      .replace(/<\/?b[^>]*>/g, '*')
      .replace(/<\/?em[^>]*>/g, '_')
      .replace(/<\/?i[^>]*>/g, '_')
      .replace(/<\/?u[^>]*>/g, '~$&~');

    pastedData = pastedData.replace(/\n{2,}/g, '\n\n').trim();

    const textarea = this.messageTextarea.nativeElement as HTMLTextAreaElement
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    textarea.value = textarea.value.substring(0, start) + pastedData + textarea.value.substring(end);

    textarea.setSelectionRange(start + pastedData.length, start + pastedData.length);
    textarea.focus();
    this.updateMessage();
  }

  private loadGroups(): void {
    this.groupsService.getList().subscribe((groups) => {
      this.groups = groups;
    });
  }

  loadTags(): void {
    this.tagService.getList().subscribe((tags) => {
      this.tags = tags;
    });
  }

  loadDepartments(): void {
    this.departmentService.getDepartmentsByChannel(this.channel._id)
      .pipe(takeUntil(this.destroy$))
      .subscribe((departments) => {
        if (this.isAttendant() || this.isSupervisor()) {
          const ids = this.getIDDepartmentsUserLogged();
          this.departments = departments.filter((department) => {
            return ids.includes(department._id);
          })
        } else {
          this.departments = departments;
        }
      });
  }

  loadUsersByDepartment() {
    const idDepartment = this.itemForm.get('value')?.value || this.itemForm.get('department')?.value;
    if (idDepartment) {
      this.teamService.getByDepartment(idDepartment)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (teams: Team[]) => {
            this.usersByDepartment = teams.map(({ agents }) => agents).flat();
          },
        });
    } else {
      this.usersByDepartment = [];
    }
  }

  loadFunnels() {
    this.funnelService.getList()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (funnels: Array<Funnel>) => {
          this.funnels = funnels;
        },
      });
  }

  loadUsers() {
    this.userService.getList()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (users: Array<User>) => {
          this.users = users;
        },
      });
  }

  loadProducts() {
    this.productService.getList()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (products: Array<Product>) => {
          this.products = products;
        },
      });
  }

  loadLeadSources() {
    this.leadSourceService.getList()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (leadSources: Array<LeadSource>) => {
          this.leadSources = leadSources;
        },
      });
  }

  loadStagesByFunnel() {
    const idFunnel = this.itemForm.get('funnel').value;
    if (idFunnel) {
      this.stageService.getStagesByFunnel(idFunnel)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (stages: Array<Stage>) => {
            this.stages = stages;
          },
          error: (err) => this.alertService.error(err.error.message),
        });
    } else {
      this.stages = new Array<Stage>();
    }
  }

  applyFormat(format: string) {
    const textarea = this.messageTextarea.nativeElement as HTMLTextAreaElement;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = textarea.value.substring(start, end);

    if (!selectedText) return;

    let formattedText = '';

    switch (format) {
      case 'bold':
        formattedText = `*${selectedText}*`;
        break;
      case 'italic':
        formattedText = `_${selectedText}_`;
        break;
      case 'underline':
        formattedText = `~${selectedText}~`;
        break;
      default:
        return;
    }

    textarea.value =
      textarea.value.substring(0, start) +
      formattedText +
      textarea.value.substring(end);

    textarea.setSelectionRange(start + formattedText.length, start + formattedText.length);
    textarea.focus();
    this.updateMessage();
  }

  updateMessage() {
    const textarea = this.messageTextarea.nativeElement as HTMLTextAreaElement;
    const messageWithoutLineBreaks = textarea.value;
    this.itemForm.patchValue({ value: messageWithoutLineBreaks });
  }

  toogleContainerEmoji(): void {
    clearTimeout(this.clearTimeout)

    this.showContainerEmoji = !this.showContainerEmoji;

    this.clearTimeout = setTimeout(() => {
      this.addClickOutsideListener();
    })
  }

  addClickOutsideListener() {
    this.clickOutsideListener = this.renderer.listen('document', 'click', (event: MouseEvent) => {
      if (this.showContainerEmoji && this.emojiContainer) {
        const modalElement = this.emojiContainer.nativeElement as HTMLElement;
        const clickedInside = modalElement?.contains(event.target as Node);

        if (!clickedInside) {
          this.closedContainerEmoji();
        }
      }
    });
  }

  closedContainerEmoji(): void {
    this.showContainerEmoji = false;
    this.clickOutsideListener()
    this.clickOutsideListener = null
  }

  addEmoji(event: any): void {
    let message = this.itemForm.get('value').value;
    if (!message) {
      message = '';
    }
    message += event.emoji.native;
    this.itemForm.get('value').setValue(message);
  }

  insertRequestOnMessage(request) {
    const value = this.itemForm.get('value').value;
    if (value) {
      this.itemForm.get('value').setValue(value + ' {{' + request + '}}');
    } else {
      this.itemForm.get('value').setValue('{{' + request + '}}');
    }
  }

  setTemplateMessage(templateMessage: MetadataMessage) {
    this.itemForm.get('template').setValue(templateMessage.template);
    this.itemForm.get('templateParamsHeader').setValue(templateMessage.headerParams);
    this.itemForm.get('templateParamsBody').setValue(templateMessage.bodyParams);
    this.itemForm.get('templateParamsButton').setValue(templateMessage.buttonParams);
    this.itemForm.get('previewText').setValue(templateMessage.previewText);
  }

  changeTemplate() {
    this.showPreviewTemplate = false;
    this.itemForm.get('template').setValue(null);
    this.itemForm.get('templateParamsHeader').setValue(null);
    this.itemForm.get('templateParamsBody').setValue(null);
    this.itemForm.get('templateParamsButton').setValue(null);
    this.itemForm.get('previewText').setValue(null);
  }

  addKeyword(event: any): void {
    const targetEl = event.target as HTMLInputElement;
    const value = (targetEl.value || '').trim();

    if (value) {
      this.keywords.push(value);
    }
    event.target.value = '';
  }

  removeTriggerKeyword(value: string): void {
    const index = this.keywords.indexOf(value);

    if (index >= 0) {
      this.keywords.splice(index, 1);

    }
  }

  isRequired(): boolean {
    return this.itemForm.get('required').value === true;
  }

  isWhatsappPhone(): boolean {
    return this.itemForm.get('type').value === WebhookIntegrationItemType.WHATSAPP_PHONE;
  }

  isContactName(): boolean {
    return this.itemForm.get('type').value === WebhookIntegrationItemType.CONTACT_NAME;
  }

  isAddTag(): boolean {
    return this.itemForm.get('type').value === WebhookIntegrationItemType.ADD_TAG;
  }

  isAddContactGroup(): boolean {
    return this.itemForm.get('type').value === WebhookIntegrationItemType.ADD_CONTACT_GROUP;
  }

  isRemoveContactGroup(): boolean {
    return this.itemForm.get('type').value === WebhookIntegrationItemType.REMOVE_CONTACT_GROUP;
  }

  isStartAttendance(): boolean {
    return this.itemForm.get('type').value === WebhookIntegrationItemType.START_ATTENDANCE;
  }

  isTransferAttendance(): boolean {
    return this.itemForm.get('type').value === WebhookIntegrationItemType.TRANSFER_ATTENDANCE;
  }

  isCloseAttendance(): boolean {
    return this.itemForm.get('type').value === WebhookIntegrationItemType.CLOSE_ATTENDANCE;
  }

  isCreateOpportunity(): boolean {
    return this.itemForm.get('type').value === WebhookIntegrationItemType.CREATE_OPPORTUNITY;
  }

  isRemoveTag(): boolean {
    return this.itemForm.get('type').value === WebhookIntegrationItemType.REMOVE_TAG;
  }

  isSendMessage(): boolean {
    return this.itemForm.get('type').value === WebhookIntegrationItemType.SEND_MESSAGE;
  }

  isCreateTracking(): boolean {
    return this.itemForm.get('type').value === WebhookIntegrationItemType.CREATE_TRACKING;
  }

  isAddTemplate(): boolean {
    return this.itemForm.get('type').value === WebhookIntegrationItemType.ADD_TEMPLATE;
  }

  isTriggerMessage(): boolean {
    return this.itemForm.get('type').value === WebhookIntegrationItemType.ADD_TRIGGER_MESSAGE;
  }

  isOriginLead(): boolean {
    return this.itemForm.get('type').value === WebhookIntegrationItemType.ORIGIN_LEAD;
  }

  getButtonActionsFormArray() {
    return this.itemForm.get('buttonActions') as any;
  }

  getLabel(): string {
    return this.itemForm.get('label').value;
  }

  getMessage(): string {
    return this.itemForm.get('message').value;
  }

  isTypeMessageMedia(): boolean {
    return this.itemForm.get('typeMessage').value && this.itemForm.get('typeMessage').value !== 'text';
  }

  // ------------------------------------------------------------------ RCS (SPEC-2026-10-rcs-webhook-integration-messages)

  readonly RCS_UPLOAD_TYPE = UploadTypeEnum.MESSAGE;

  private requestVariablesSource: object;
  private requestVariablesCache: RcsVariableOption[] = [];

  /** The action is composed in the RCS editor: the form gave it the editor's group (RCS channel). */
  hasRcsEditor(): boolean {
    return !!this.itemForm?.get('rcsData');
  }

  get rcsGroup(): FormGroup {
    return this.itemForm.get('rcsData') as FormGroup;
  }

  /**
   * The fields of the selected sample request, offered by the RCS editor as `{{field}}` variables.
   * Rebuilt only when another request is selected, so the editor's input keeps its identity.
   */
  get requestVariables(): RcsVariableOption[] {
    if (this.webhookRequests !== this.requestVariablesSource) {
      this.requestVariablesSource = this.webhookRequests;
      this.requestVariablesCache = Object.keys(this.webhookRequests ?? {}).map((key) => ({ token: key, label: key }));
    }
    return this.requestVariablesCache;
  }

}
